/* ============================================================
   SAM-198 — service editor v2 (mock 3 v2, approved 2026-10-01): pure
   helpers behind the reorganised sections. No React, so the rules are
   unit-testable (see __tests__/editorV2.test.ts).

   Nothing here changes the save payload: the form keeps the same fields
   (`booking_window_start` in hours, `booking_window_end` in days, both
   serialised to ISO 8601 by useServiceEditor's formToServiceData —
   "PT2H" / "P30D", the format engine_sync.py and lambda_function.py
   read), and the hours/resources helpers only read or reshape form state.
   ============================================================ */
import { computeWeekdayWindows, WEEKDAYS, type OperatingHours } from '../../hooks/useServices';
import type { OperatingHourForm } from '../../hooks/useServiceEditor';
import type { ResourceForm } from '../../components/ServiceResourcesEditor';

// --- Booking window as a sentence ("From 30 days ahead, until 1 hour before") ---

export type AheadUnit = 'days' | 'weeks';
export type LeadUnit = 'minutes' | 'hours' | 'days';

export interface WindowPart<U extends string> {
  value: string;
  unit: U;
}

const AHEAD_DAYS: Record<AheadUnit, number> = { days: 1, weeks: 7 };
const LEAD_HOURS: Record<LeadUnit, number> = { minutes: 1 / 60, hours: 1, days: 24 };

function trimNumber(n: number): string {
  return String(Math.round(n * 100) / 100);
}

/** Form `booking_window_end` (days, as typed) -> the sentence's number + unit. Weeks only when
 * the days divide evenly, so a stored P30D always reads back as "30 days". */
export function aheadToParts(days: string): WindowPart<AheadUnit> {
  const n = Number(days);
  if (days.trim() === '' || !Number.isFinite(n)) return { value: '', unit: 'days' };
  if (n > 0 && n % 7 === 0) return { value: trimNumber(n / 7), unit: 'weeks' };
  return { value: trimNumber(n), unit: 'days' };
}

/** The sentence's number + unit -> form `booking_window_end` (whole days; P{n}D on save). */
export function partsToAheadDays(value: string, unit: AheadUnit): string {
  const n = Number(value);
  if (value.trim() === '' || !Number.isFinite(n)) return '';
  return String(Math.max(0, Math.round(n * AHEAD_DAYS[unit])));
}

/** Form `booking_window_start` (hours) -> number + unit: days when a whole number of days,
 * minutes when under an hour, else hours. */
export function leadToParts(hours: string): WindowPart<LeadUnit> {
  const n = Number(hours);
  if (hours.trim() === '' || !Number.isFinite(n)) return { value: '', unit: 'hours' };
  if (n > 0 && n % 24 === 0) return { value: trimNumber(n / 24), unit: 'days' };
  if (n > 0 && n < 1) return { value: trimNumber(n * 60), unit: 'minutes' };
  return { value: trimNumber(n), unit: 'hours' };
}

/** The sentence's number + unit -> form `booking_window_start` (hours; PT…H/M on save). */
export function partsToLeadHours(value: string, unit: LeadUnit): string {
  const n = Number(value);
  if (value.trim() === '' || !Number.isFinite(n)) return '';
  const hours = Math.max(0, n * LEAD_HOURS[unit]);
  // Minutes are stored as fractional hours; keep enough precision for hoursToISODuration's rounding.
  return String(Math.round(hours * 10000) / 10000);
}

function plural(n: number, one: string, many: string): string {
  return `${trimNumber(n)} ${n === 1 ? one : many}`;
}

/** Plain-language example under the sentence, from the form's own values. */
export function describeBookingWindow(aheadDays: string, leadHours: string, today: Date): string {
  const fmt = (d: Date) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  const ahead = Number(aheadDays);
  const parts: string[] = [];
  if (aheadDays.trim() !== '' && Number.isFinite(ahead) && ahead > 0) {
    const last = new Date(today);
    last.setDate(last.getDate() + ahead);
    parts.push(`Today, ${fmt(today)}, a guest can book any open date up to ${fmt(last)}.`);
  } else {
    parts.push('No limit on how far ahead guests can book.');
  }
  const lead = leadToParts(leadHours);
  const leadN = Number(lead.value);
  if (lead.value !== '' && leadN > 0) {
    const word = lead.unit === 'days' ? plural(leadN, 'day', 'days')
      : lead.unit === 'minutes' ? plural(leadN, 'minute', 'minutes') : plural(leadN, 'hour', 'hours');
    parts.push(`Each date or time closes to new bookings ${word} before it starts.`);
  } else {
    parts.push('Guests can book right up to the start.');
  }
  return parts.join(' ');
}

// --- Custom hours ---

/** "Copy Monday to all days": every other day gets the source day's on/off, hours and extra
 * ranges. Label-only session metadata (SessionsSection) stays per day. */
export function copyDayToAll(
  hours: Record<string, OperatingHourForm>,
  from: string,
): Record<string, OperatingHourForm> {
  const src = hours[from];
  if (!src) return hours;
  return Object.fromEntries(Object.entries(hours).map(([key, day]) => [
    key,
    key === from ? day : {
      ...day,
      enabled: src.enabled,
      open: src.open,
      close: src.close,
      extra_windows: src.extra_windows.map((w) => ({ ...w })),
    },
  ]));
}

function toMinutes(clock: string): number {
  const [h, m] = clock.split(':').map((x) => Number(x) || 0);
  return h * 60 + m;
}

export interface DayBars {
  key: string;
  short: string;
  /** [start, end] in minutes from midnight, end <= 1440; a range past midnight continues on the next day. */
  ranges: Array<[number, number]>;
}

/** Weekly bars on the right of Booking & hours — same split engine_sync.py's weekday_windows uses. */
export function weekBars(hours: OperatingHours): DayBars[] {
  const windows = computeWeekdayWindows(hours);
  return WEEKDAYS.map(({ key, label }) => ({
    key,
    short: label,
    ranges: (windows[key] || [])
      .map(([a, b]) => [toMinutes(a), Math.min(1440, toMinutes(b))] as [number, number])
      .filter(([a, b]) => b > a)
      .sort((x, y) => x[0] - y[0]),
  }));
}

// --- Resources ---

export interface ResourceWord {
  singular: string;
  plural: string;
}

/** The word each setup uses for what gets booked (mock 3 v2: "tables, courts, tee starts…"). */
const RESOURCE_WORDS: Record<string, ResourceWord> = {
  cabana: { singular: 'cabana', plural: 'cabanas' },
  tables: { singular: 'table', plural: 'tables' },
  teetime: { singular: 'tee start', plural: 'tee starts' },
  equipment: { singular: 'court or space', plural: 'courts and spaces' },
  stay: { singular: 'room', plural: 'rooms' },
  class: { singular: 'room', plural: 'rooms' },
  private: { singular: 'provider', plural: 'providers' },
};

export function resourceWord(subtype: string): ResourceWord {
  return RESOURCE_WORDS[subtype] || { singular: 'resource', plural: 'resources' };
}

export function capitalize(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

/** Capacity read from Resources: how many and the guests they hold in total (blank = 0). */
export function capacityFromResources(resources: ResourceForm[]): { count: number; guests: number } {
  return {
    count: resources.length,
    guests: resources.reduce((t, r) => t + (Number(r.capacity) || 0), 0),
  };
}

// --- Pricing ---

/** The one currency the Pricing section shows: the first guest type's, else the first add-on's. */
export function pricingCurrency(form: { booking_options: Array<{ currency: string }>; add_ons: Array<{ currency: string }> }): string {
  return form.booking_options[0]?.currency || form.add_ons[0]?.currency || 'USD';
}

/** "Currency once": the same currency on every guest type and add-on (each still stores its own). */
export function withCurrency<F extends { booking_options: Array<{ currency: string }>; add_ons: Array<{ currency: string }> }>(
  form: F,
  currency: string,
): F {
  return {
    ...form,
    booking_options: form.booking_options.map((o) => ({ ...o, currency })),
    add_ons: form.add_ons.map((a) => ({ ...a, currency })),
  };
}

/** True when guest types or add-ons disagree on currency (older data) — shown as a note. */
export function hasMixedCurrency(form: { booking_options: Array<{ currency: string }>; add_ons: Array<{ currency: string }> }): boolean {
  const all = [...form.booking_options, ...form.add_ons].map((x) => x.currency).filter(Boolean);
  return new Set(all).size > 1;
}

/** Season tab text: its label, else "Season n". */
export function seasonTabLabel(season: { label_en: string; label_es: string }, index: number): string {
  return season.label_en.trim() || season.label_es.trim() || `Season ${index + 1}`;
}

/** Any resource with its own price / included / max pax (SAM-591 unit overrides). */
export function hasUnitOverrides(resources: ResourceForm[]): boolean {
  return resources.some((r) => !!(r.price?.trim() || r.included_pax?.trim() || r.max_pax?.trim()));
}

/** Turning "Different price per …" off: every resource falls back to the service's unit price. */
export function clearUnitOverrides(resources: ResourceForm[]): ResourceForm[] {
  return resources.map((r) => ({ ...r, price: '', included_pax: '', max_pax: '' }));
}
